import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const ACCENT_OPTIONS = ['blue', 'green', 'violet', 'amber', 'rose', 'cyan', 'pink', 'custom'];

function getStoredTheme() {
  try { return localStorage.getItem('fitndex_theme') || 'light'; } catch { return 'light'; }
}

function getStoredAccent() {
  try {
    const stored = localStorage.getItem('fitndex_accent');
    return ACCENT_OPTIONS.includes(stored) ? stored : 'violet';
  } catch { return 'violet'; }
}

function getStoredCustomColor() {
  try { return localStorage.getItem('fitndex_custom_accent') || '#5b9dff'; } catch { return '#5b9dff'; }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  const [accent, setAccentState] = useState(getStoredAccent);
  const [customColor, setCustomColorState] = useState(getStoredCustomColor);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('fitndex_theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-accent', accent);
    localStorage.setItem('fitndex_accent', accent);
  }, [accent]);

  // Restore custom CSS variable on every render (and on initial mount for 'custom' accent)
  useEffect(() => {
    document.documentElement.style.setProperty('--custom-accent-color', customColor);
    localStorage.setItem('fitndex_custom_accent', customColor);
  }, [customColor]);

  const setTheme = useCallback((next) => {
    if (next === 'dark' || next === 'light') setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const setAccent = useCallback((next) => {
    if (ACCENT_OPTIONS.includes(next)) setAccentState(next);
  }, []);

  const setCustomColor = useCallback((hex) => {
    setCustomColorState(hex);
    document.documentElement.style.setProperty('--custom-accent-color', hex);
  }, []);

  const value = {
    theme,
    accent,
    customColor,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    accentOptions: ACCENT_OPTIONS,
    setTheme,
    toggleTheme,
    setAccent,
    setCustomColor,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
